// ==============================================================
// GOLF MOTION LAB — Scoring system (shared client & server)
// All pillar scores are 0-100. Global = weighted avg of the 4 pillars.
// ==============================================================

export type ZoneKey = "optimal" | "functional" | "attention" | "limiting" | "critical";

export const PILLAR_WEIGHTS = {
  mobility: 0.25,
  strength: 0.25,
  performance: 0.25,
  motorControl: 0.25,
} as const;

export const ZONE_COLORS: Record<ZoneKey, string> = {
  optimal: "#6FA872",
  functional: "#97B46F",
  attention: "#D9A441",
  limiting: "#D27A3C",
  critical: "#B63A2D",
};

export const ZONE_LABELS: Record<ZoneKey, string> = {
  optimal: "Óptimo",
  functional: "Funcional",
  attention: "Atención",
  limiting: "Limitante",
  critical: "Crítico",
};

export function getZone(score: number): ZoneKey {
  if (score >= 85) return "optimal";
  if (score >= 70) return "functional";
  if (score >= 50) return "attention";
  if (score >= 30) return "limiting";
  return "critical";
}

/** Clamp to [0,100] and round. */
export function clampScore(s: number): number {
  if (!Number.isFinite(s)) return 0;
  return Math.max(0, Math.min(100, Math.round(s)));
}

/** Weighted global score from the 4 pillars. */
export function computeGlobalScore(p: {
  mobility: number;
  strength: number;
  performance: number;
  motorControl: number;
}): number {
  const score =
    p.mobility * PILLAR_WEIGHTS.mobility +
    p.strength * PILLAR_WEIGHTS.strength +
    p.performance * PILLAR_WEIGHTS.performance +
    p.motorControl * PILLAR_WEIGHTS.motorControl;
  return clampScore(score);
}

// --------------------------------------------------------------
// Metric-level scoring: Linear mapping from a clinical range.
// `poor` → 0, `excellent` → 100. Inverse mode when "lower is better".
// --------------------------------------------------------------
export interface RangeSpec {
  poor: number;
  excellent: number;
  inverse?: boolean; // true when lower value = better (e.g., asymmetry %)
}

export function scoreFromRange(value: number, spec: RangeSpec): number {
  if (!Number.isFinite(value)) return 0;
  const { poor, excellent, inverse } = spec;
  if (poor === excellent) return 50;
  let ratio: number;
  if (inverse) {
    ratio = (poor - value) / (poor - excellent);
  } else {
    ratio = (value - poor) / (excellent - poor);
  }
  return clampScore(ratio * 100);
}

/** Average scores of a list of sub-metrics. */
export function averageScores(scores: number[]): number {
  if (scores.length === 0) return 0;
  const sum = scores.reduce((acc, s) => acc + s, 0);
  return clampScore(sum / scores.length);
}

// --------------------------------------------------------------
// MOBILITY — reference ranges
// --------------------------------------------------------------
export const MOBILITY_RANGES = {
  toeTouch: { poor: 0, excellent: 15 } as RangeSpec, // cm past toes, >15 excellent
  shoulderMobility: { poor: 20, excellent: 0, inverse: true } as RangeSpec, // cm gap between fists
  thoracicRotation: { poor: 30, excellent: 60 } as RangeSpec, // degrees
  hipInternalRotation: { poor: 20, excellent: 45 } as RangeSpec,
  ankleDorsiflexion: { poor: 0, excellent: 12 } as RangeSpec, // cm wall test
  pelvicTilt: { poor: 5, excellent: 20 } as RangeSpec, // range in degrees
};

// --------------------------------------------------------------
// STRENGTH — reference ranges (VALD-oriented)
// --------------------------------------------------------------
export const STRENGTH_RANGES = {
  gripStrength: { poor: 25, excellent: 55 } as RangeSpec, // kg
  cmjHeight: { poor: 20, excellent: 45 } as RangeSpec, // cm
  isquioBodyweightRatio: { poor: 1.2, excellent: 2.0 } as RangeSpec, // N/BW
  quadStrengthRatio: { poor: 2.0, excellent: 3.5 } as RangeSpec,
  trunkStability: { poor: 30, excellent: 75 } as RangeSpec, // secs plank / score
  bilateralAsymmetry: { poor: 20, excellent: 5, inverse: true } as RangeSpec, // % delta
};

// --------------------------------------------------------------
// PERFORMANCE — TrackMan-oriented
// --------------------------------------------------------------
export const PERFORMANCE_RANGES = {
  clubheadSpeed: { poor: 80, excellent: 115 } as RangeSpec, // mph Driver
  ballSpeed: { poor: 120, excellent: 170 } as RangeSpec,
  smashFactor: { poor: 1.35, excellent: 1.5 } as RangeSpec,
  carryDistance: { poor: 180, excellent: 280 } as RangeSpec, // yards
  dispersion: { poor: 40, excellent: 10, inverse: true } as RangeSpec, // yards
  attackAngle: { poor: 0, excellent: 5 } as RangeSpec, // Driver attack angle °
};

// --------------------------------------------------------------
// MOTOR CONTROL — OnForm / swing quality
// --------------------------------------------------------------
export const MOTOR_CONTROL_RANGES = {
  kinematicSequence: { poor: 0, excellent: 100 } as RangeSpec, // scored 0-100 by coach
  postureSetup: { poor: 0, excellent: 100 } as RangeSpec,
  balanceFinish: { poor: 0, excellent: 100 } as RangeSpec,
  tempoRatio: { poor: 0.5, excellent: 0, inverse: true } as RangeSpec, // diff to 3:1 target
  xFactor: { poor: 20, excellent: 50 } as RangeSpec, // shoulder-hip separation °
  repeatability: { poor: 0, excellent: 100 } as RangeSpec,
};
